import React, { useState, useEffect } from 'react';
import { User, Mail, Search, BarChart2 } from 'lucide-react';
import { Modal, Badge, LoadingSpinner, EmptyState } from './index';
import { Respondent } from '../../types';

interface RespondersModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    fetchResponders: () => Promise<Respondent[]>;
    onNavigateToAnalytics?: (respondentId: string) => void;
}

export const RespondersModal: React.FC<RespondersModalProps> = ({
    isOpen,
    onClose,
    title,
    fetchResponders,
    onNavigateToAnalytics,
}) => {
    const [respondents, setResponders] = useState<Respondent[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isOpen) {
            const loadResponders = async () => {
                setLoading(true);
                try {
                    const data = await fetchResponders();
                    // Filter out invalid items (null, or missing required fields) to ensure proper Empty State
                    const validData = Array.isArray(data)
                        ? data.filter(r => r && r.name && r.name.trim() !== '')
                        : [];
                    setResponders(validData);
                } catch (error) {
                    console.error('Failed to fetch respondents:', error);
                } finally {
                    setLoading(false);
                }
            };
            loadResponders();
        } else {
            setResponders([]);
            setSearchQuery('');
        }
    }, [isOpen, fetchResponders]);

    const filteredResponders = respondents.filter(
        (r) =>
            (r.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (r.email || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="large"
        >
            <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                    <Search
                        size={18}
                        style={{
                            position: 'absolute',
                            right: 'var(--space-3)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--neutral-400)',
                        }}
                    />
                    <input
                        type="text"
                        placeholder="חיפוש לפי שם או אימייל..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ paddingRight: 'var(--space-10)', width: '100%' }}
                    />
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 'var(--space-10)', textAlign: 'center' }}>
                    <LoadingSpinner text="טוען משיבים..." />
                </div>
            ) : filteredResponders.length === 0 ? (
                <EmptyState
                    icon={<User size={48} />}
                    title="לא נמצאו משיבים"
                    description={searchQuery ? 'נסה לשנות את פרמטרי החיפוש' : 'אין משיבים רשומים לסקר זה'}
                />
            ) : (
                <div className="table-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>שם</th>
                                <th>אימייל</th>
                                <th>סקר / קבוצה</th>
                                <th>סטטוס</th>
                                {onNavigateToAnalytics && <th style={{ width: '50px' }}></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredResponders.map((r) => (
                                <tr key={r.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 500 }}>
                                            <User size={14} color="var(--primary-600)" />
                                            {r.name || 'Unknown'}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--neutral-600)' }}>
                                            <Mail size={14} />
                                            {r.email || '-'}
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--neutral-500)' }}>
                                            {r.instance_name || r.survey_instance_name || '-'}
                                        </span>
                                    </td>
                                    <td>
                                        <Badge variant={r.status === 'done' || r.status === 'completed' ? 'success' : 'neutral'}>
                                            {r.status === 'done' || r.status === 'completed' ? 'הושלם' : 'בביצוע'}
                                        </Badge>
                                    </td>
                                    {onNavigateToAnalytics && (
                                        <td>
                                            {(r.status === 'done' || r.status === 'completed') && (
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => {
                                                        onNavigateToAnalytics(r.id);
                                                        onClose(); // Close modal when navigating
                                                    }}
                                                    title="צפה בתוצאות אישיות"
                                                    style={{ color: 'var(--primary-400)' }}
                                                >
                                                    <BarChart2 size={16} />
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};
